<template>
  <div class="cost-center-card" :class="{ 'is-selected': selected }" @click="$emit('edit', costCenter)">
    <div class="cost-center-card-header">
      <div class="cost-center-card-identity">
        <div class="cost-center-card-checkbox" @click.stop>
          <b-form-checkbox :checked="selected" @change="$emit('select', costCenter)" />
        </div>
        <div class="cost-center-card-icon" :style="'background-color:' + costCenter.color">
          <i :class="costCenter.icon"></i>
        </div>
        <div class="cost-center-card-info">
          <Tooltip :title="costCenter.name">
            <span class="cost-center-card-name">{{ costCenter.name }}</span>
          </Tooltip>
          <div class="cost-center-card-type">
            <Badge :title="costCenter.typeName" :type="isExpense ? 'danger' : 'success'" size="small" />
          </div>
        </div>
      </div>

      <div class="cost-center-card-actions">
        <Tooltip title="Editar Categoria">
          <span class="cost-center-card-btn" @click.stop="$emit('edit', costCenter)">
            <i class="fa-solid fa-pen-to-square"></i>
          </span>
        </Tooltip>
        <Tooltip title="Excluir Categoria">
          <span class="cost-center-card-btn cost-center-card-btn-danger" @click.stop="$emit('delete', costCenter)">
            <i class="fa-solid fa-trash"></i>
          </span>
        </Tooltip>
      </div>
    </div>

    <div class="cost-center-card-toggle" :class="{ 'is-open': expanded }" @click.stop="$emit('toggle', costCenter)">
      <span>{{ chartOfAccountsLabel }}</span>
      <i class="fa-solid fa-chevron-down cost-center-card-toggle-icon"></i>
    </div>

    <div v-if="expanded" class="cost-center-card-chart" @click.stop>
      <slot name="chart"></slot>
    </div>
  </div>
</template>

<script>
import Badge from "@/ui/layout/Badge";
import Tooltip from "@/ui/layout/Tooltip";

export default {
  name: "CostCenterCard",
  components: { Badge, Tooltip },
  props: {
    costCenter: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isExpense() {
      return this.costCenter.typeName === "Despesa";
    },
    chartOfAccountsLabel() {
      const total = this.costCenter.totalChartOfAccounts || 0;
      if (total === 1) return "1 plano de conta";
      return `${total} planos de conta`;
    },
  },
};
</script>

<style scoped>
.cost-center-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--bg-base);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.cost-center-card:hover {
  box-shadow: var(--shadow-md);
}

.cost-center-card.is-selected {
  border-color: var(--color-brand);
}

.cost-center-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.cost-center-card-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.cost-center-card-checkbox {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.cost-center-card-checkbox >>> .custom-control {
  margin: 0;
}

.cost-center-card-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-size: 16px;
  flex-shrink: 0;
}

.cost-center-card-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.cost-center-card-info >>> .nx-tooltip-trigger {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.cost-center-card-name {
  display: block;
  max-width: 100%;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cost-center-card-type >>> .badge {
  margin-left: 0;
}

.cost-center-card-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.cost-center-card-btn {
  font-size: 15px;
  cursor: pointer;
  color: var(--text-muted);
  transition: color 0.15s;
}

.cost-center-card-btn:hover {
  color: var(--color-brand);
}

.cost-center-card-btn-danger {
  color: var(--color-danger);
}

.cost-center-card-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-top: 1px solid var(--border-subtle);
  padding-top: 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  transition: color 0.15s;
}

.cost-center-card-toggle:hover {
  color: var(--color-brand);
}

.cost-center-card-toggle.is-open {
  color: var(--color-brand);
}

.cost-center-card-toggle-icon {
  font-size: 11px;
  color: var(--text-muted);
  transition: transform 0.15s;
}

.cost-center-card-toggle.is-open .cost-center-card-toggle-icon {
  transform: rotate(180deg);
  color: var(--color-brand);
}

.cost-center-card-chart {
  animation: cost-center-card-fade-in 0.15s ease-out;
}

@keyframes cost-center-card-fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
